/**
 * Knop "Presentatie (PPT)" + dialoog om optioneel beelden toe te voegen (2 nov 2026).
 *
 * Joris: "versie met afbeeldingen, zodat het iets meer leeft" — en "ook mogelijk om geen
 * afbeeldingen te uploaden". Drie vaste plekken: cover (1), debriefing (1), referenties (max 4).
 * Bron per beeld: zelf uploaden of een still uit een video in de videotheek. Niets wordt
 * opgeslagen; de beelden gaan alleen in het deck.
 */
import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import CloseIcon from '@mui/icons-material/Close';
import UploadIcon from '@mui/icons-material/Upload';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import RefreshIcon from '@mui/icons-material/Refresh';
import toast from 'react-hot-toast';
import { downloadOffertePresentatie, haalStill, verkleinAfbeelding, zoekVideotheek } from './offertePresentatie';

const PAARS = '#6C49BA';
const MAX_TOTAAL = 6;
const PLEKKEN = [
  { key: 'cover', label: 'Cover', uitleg: 'Achtergrond van de eerste slide', max: 1 },
  { key: 'debriefing', label: 'Debriefing', uitleg: 'Boven "In één zin"', max: 1 },
  { key: 'referentie', label: 'Referenties', uitleg: 'Eigen slide "Zo kan het eruitzien", met onderschrift', max: 4 },
];
const MOMENTEN = [0.3, 0.5, 0.7, 0.15]; // fractie van de videoduur voor "ander frame"

let volgnummer = 0;
const nieuwId = () => { volgnummer += 1; return volgnummer; };

function Tegel({ beeld, onWeg, onBijschrift, onAnderFrame, metBijschrift }) {
  return (
    <Box sx={{ width: 168 }}>
      <Box sx={{ position: 'relative', width: 168, height: 95, borderRadius: 1, overflow: 'hidden', bgcolor: '#f1f0ee' }}>
        {beeld.preview
          ? <Box component="img" src={beeld.preview} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : <Box sx={{ display: 'grid', placeItems: 'center', height: '100%' }}><CircularProgress size={18} /></Box>}
        <IconButton size="small" onClick={onWeg} aria-label="Beeld weghalen"
          sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'rgba(255,255,255,.9)', '&:hover': { bgcolor: '#fff' } }}>
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
        {beeld.video_id && (
          <Tooltip title="Ander moment uit de video">
            <IconButton size="small" onClick={onAnderFrame} aria-label="Ander frame"
              sx={{ position: 'absolute', bottom: 4, right: 4, bgcolor: 'rgba(255,255,255,.9)', '&:hover': { bgcolor: '#fff' } }}>
              <RefreshIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      {metBijschrift && (
        <TextField size="small" variant="standard" fullWidth placeholder="Onderschrift (optioneel)"
          value={beeld.bijschrift || ''} onChange={(e) => onBijschrift(e.target.value)}
          inputProps={{ maxLength: 90, style: { fontSize: 12 } }} sx={{ mt: 0.5 }} />
      )}
    </Box>
  );
}

function VideotheekKiezer({ onKies, onSluit }) {
  const [q, setQ] = useState('');
  const [videos, setVideos] = useState(null);
  const [stills, setStills] = useState({});
  const levend = useRef(true);
  const zoekNr = useRef(0);

  useEffect(() => () => { levend.current = false; }, []);
  useEffect(() => {
    // Alleen het antwoord op de laatste zoekterm telt: een trage oude zoekactie mag de
    // resultaten voor de nieuwe klantnaam niet overschrijven. Direct ophogen (niet pas na de
    // debounce), zodat ook antwoorden die binnen de 250 ms binnenkomen vervallen.
    zoekNr.current += 1;
    const nr = zoekNr.current;
    const actueel = () => levend.current && nr === zoekNr.current;
    const timer = setTimeout(() => {
      zoekVideotheek(q).then((v) => actueel() && setVideos(v.slice(0, 12)))
        .catch((e) => { if (actueel()) { toast.error(e.message); setVideos([]); } });
    }, 250);
    return () => clearTimeout(timer);
  }, [q]);
  // Voorvertoningen drie tegelijk ophalen: elke still is een ffmpeg-aanroep op de server.
  useEffect(() => {
    if (!videos) return undefined;
    let gestopt = false;
    const rij = videos.filter((v) => !stills[v.id]).map((v) => v.id);
    const werker = async () => {
      while (rij.length && !gestopt) {
        const id = rij.shift();
        try {
          const url = await haalStill(id);
          if (!gestopt && levend.current) setStills((s) => ({ ...s, [id]: url }));
        } catch { if (!gestopt && levend.current) setStills((s) => ({ ...s, [id]: 'fout' })); }
      }
    };
    Promise.all([werker(), werker(), werker()]);
    return () => { gestopt = true; };
  }, [videos]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box sx={{ mt: 1.5, p: 1.5, border: '1px solid #e7e5e4', borderRadius: 1.5 }}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1.5 }}>
        <TextField size="small" autoFocus fullWidth placeholder="Zoek op klant, projectnummer of bestandsnaam"
          value={q} onChange={(e) => setQ(e.target.value)} />
        <Button size="small" onClick={onSluit} sx={{ textTransform: 'none' }}>Sluiten</Button>
      </Box>
      {videos === null && <CircularProgress size={18} />}
      {videos?.length === 0 && <Typography variant="body2" color="text.secondary">Geen video's gevonden.</Typography>}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 1.5 }}>
        {(videos || []).map((v) => (
          <Box key={v.id} component="button" type="button" disabled={stills[v.id] === 'fout'}
            onClick={() => onKies(v, stills[v.id])}
            sx={{ all: 'unset', cursor: 'pointer', borderRadius: 1, '&:focus-visible': { outline: `2px solid ${PAARS}` },
              '&:disabled': { opacity: 0.4, cursor: 'default' } }}>
            <Box sx={{ height: 84, borderRadius: 1, overflow: 'hidden', bgcolor: '#f1f0ee', display: 'grid', placeItems: 'center' }}>
              {stills[v.id] && stills[v.id] !== 'fout'
                ? <Box component="img" src={stills[v.id]} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : stills[v.id] === 'fout'
                  ? <Typography variant="caption" color="text.secondary">Geen still</Typography>
                  : <CircularProgress size={16} />}
            </Box>
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, lineHeight: 1.3, fontWeight: 600 }} noWrap>
              {v.klant || v.titel}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3 }} noWrap>
              {[v.projectnummer, v.titel].filter(Boolean).join(' · ')}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default function PresentatieKnop({ offerNumber, source, senderName, companyName, size, sx }) {
  const [open, setOpen] = useState(false);
  const [beelden, setBeelden] = useState([]);
  const [kiezer, setKiezer] = useState(null); // plek waarvoor de videotheek open staat
  const [bezig, setBezig] = useState(false);
  const invoer = useRef({});
  const frameVerzoek = useRef({}); // beeld-id → nummer van het laatste still-verzoek

  const totaal = beelden.length;
  const vanPlek = (plek) => beelden.filter((b) => b.plek === plek);
  const kanErbij = (plek) => totaal < MAX_TOTAAL && vanPlek(plek).length < PLEKKEN.find((p) => p.key === plek).max;
  const wijzig = (id, velden) => setBeelden((bs) => bs.map((b) => (b.id === id ? { ...b, ...velden } : b)));

  const upload = async (plek, bestanden) => {
    const ruimte = Math.min(MAX_TOTAAL - totaal, PLEKKEN.find((p) => p.key === plek).max - vanPlek(plek).length);
    for (const bestand of Array.from(bestanden || []).slice(0, ruimte)) {
      try {
        const data = await verkleinAfbeelding(bestand);
        const id = nieuwId();
        setBeelden((bs) => [...bs, { id, plek, data, preview: data, bijschrift: '' }]);
      } catch (e) { toast.error(e.message); }
    }
  };

  const kiesVideo = (plek, video, preview) => {
    const id = nieuwId();
    setBeelden((bs) => [...bs, { id, plek, video_id: video.id, duur: video.duur, moment: 0,
      preview: preview && preview !== 'fout' ? preview : null, bijschrift: video.klant || '' }]);
    setKiezer(null);
    if (!preview || preview === 'fout') {
      frameVerzoek.current[id] = 1;      // zelfde volgnummer als "Ander frame"
      haalStill(video.id)
        .then((url) => { if (frameVerzoek.current[id] === 1) wijzig(id, { preview: url }); })
        .catch(() => {});
    }
  };

  const anderFrame = (beeld) => {
    const moment = (beeld.moment + 1) % MOMENTEN.length;
    const seconde = beeld.duur ? Math.round(beeld.duur * MOMENTEN[moment] * 10) / 10 : 5 + moment * 10;
    // Snel twee keer klikken: alleen het laatste verzoek mag de preview zetten, anders toont
    // de preview een ander frame dan `seconde` (en dus dan het deck) — Codex 2 nov.
    const nr = (frameVerzoek.current[beeld.id] || 0) + 1;
    frameVerzoek.current[beeld.id] = nr;
    wijzig(beeld.id, { moment, seconde, preview: null });
    haalStill(beeld.video_id, seconde)
      .then((url) => { if (frameVerzoek.current[beeld.id] === nr) wijzig(beeld.id, { preview: url }); })
      .catch(() => { if (frameVerzoek.current[beeld.id] === nr) toast.error('Kon dat moment niet ophalen'); });
  };

  const maak = async () => {
    setBezig(true);
    const ok = await downloadOffertePresentatie({
      offerNumber, source, senderName, companyName,
      afbeeldingen: beelden.filter((b) => b.data).map(({ plek, data, bijschrift }) => ({ plek, data, bijschrift })),
      videotheek: beelden.filter((b) => b.video_id).map(({ plek, video_id, seconde, bijschrift }) => ({ plek, video_id, seconde, bijschrift })),
    });
    setBezig(false);
    if (ok) setOpen(false);
  };

  return (
    <>
      <Tooltip title="Maakt een klantpresentatie (PowerPoint) met debriefing, aanpak, aannames en de prijs per onderdeel — optioneel met beelden. Lees hem na voordat je hem verstuurt.">
        <Button variant={size === 'small' ? 'text' : 'outlined'} size={size}
          startIcon={<SlideshowIcon sx={size === 'small' ? { fontSize: 16 } : undefined} fontSize="small" />}
          onClick={() => setOpen(true)} sx={{ textTransform: 'none', fontWeight: 700, ...sx }}>
          Presentatie (PPT)
        </Button>
      </Tooltip>
      <Dialog open={open} onClose={() => !bezig && setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Klantpresentatie</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Beelden zijn optioneel. Zonder beelden krijg je het gewone deck; met beelden leeft hij meer.
            Er wordt niets opgeslagen.
          </Typography>
          {PLEKKEN.map((plek) => (
            <Box key={plek.key} sx={{ py: 1.75, borderTop: '1px solid #eeecea' }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{plek.label}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {plek.uitleg}{plek.max > 1 ? ` · max ${plek.max}` : ''}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start' }}>
                {vanPlek(plek.key).map((b) => (
                  <Tegel key={b.id} beeld={b} metBijschrift={plek.key === 'referentie'}
                    onWeg={() => setBeelden((bs) => bs.filter((x) => x.id !== b.id))}
                    onBijschrift={(tekst) => wijzig(b.id, { bijschrift: tekst })}
                    onAnderFrame={() => anderFrame(b)} />
                ))}
                {kanErbij(plek.key) && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <input ref={(el) => { invoer.current[plek.key] = el; }} type="file" accept="image/*" hidden
                      multiple={plek.max > 1} onChange={(e) => { upload(plek.key, e.target.files); e.target.value = ''; }} />
                    <Button size="small" variant="outlined" startIcon={<UploadIcon />}
                      onClick={() => invoer.current[plek.key]?.click()} sx={{ textTransform: 'none' }}>Uploaden</Button>
                    <Button size="small" variant="outlined" startIcon={<VideoLibraryIcon />}
                      onClick={() => setKiezer(kiezer === plek.key ? null : plek.key)} sx={{ textTransform: 'none' }}>
                      Uit videotheek
                    </Button>
                  </Box>
                )}
              </Box>
              {kiezer === plek.key && (
                <VideotheekKiezer onKies={(v, preview) => kiesVideo(plek.key, v, preview)} onSluit={() => setKiezer(null)} />
              )}
            </Box>
          ))}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)} disabled={bezig} sx={{ textTransform: 'none' }}>Annuleren</Button>
          <Button variant="contained" onClick={maak} disabled={bezig}
            startIcon={bezig ? <CircularProgress size={16} color="inherit" /> : <SlideshowIcon />}
            sx={{ textTransform: 'none', fontWeight: 700, bgcolor: PAARS, '&:hover': { bgcolor: '#5a3aa3' } }}>
            {bezig ? 'Bezig…' : totaal ? `Maken met ${totaal} ${totaal === 1 ? 'beeld' : 'beelden'}` : 'Maken zonder beelden'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
